/**
 * Renders standard HTML select box dropdowns when the data is in a common
 * format.
 */

import $ from 'jquery'

import _ from 'lodash'
import i18n from 'scoreboard/components/i18n'
import currency from 'scoreboard/components/currency'
import config from 'scoreboard/components/config'
import selects from 'scoreboard/components/selects'
import scoringTypeIcon from 'scoreboard/components/scoring-type-icon'
import enums from 'scoreboard/components/enums'

/**
 * Appends the optionEls to the select and optionally sorts them by label
 *
 * @param {jQuery} select
 *            one or more select DOM elements
 * @param {HTMLOptionElement[]} optionEls
 *            the option elements to be appended to the select
 * @param [options]
 * 	          optional options to customize how the dropdown is rendered
 * @param [options.sort=false]
 *            indicates the options should be sorted alphabetically
 */
function append(select, optionEls, options) {
	if (_.get(options, 'sort')) {
		optionEls = _.sortBy(optionEls, option => option.label.toLowerCase())
	}
	_.forEach(optionEls, optionEl => {
		select.append(optionEl)
	})
}

var dropdown = {

	// common behavior that should be executed for all selects
	_commonBehavior: function(select, value) {
		if (!_.isUndefined(value)) {
			// treat a null value as meaning to select an option without a value
			// which the browser thinks is '' instead of null
			if (value == null) {
				select.val('')
			} else {
				select.val(value)
			}
		}
		// redraw the select without triggering an event
		selects(select)
	},

	/**
	 * Renders a dropdown using an array of objects with id and label
	 * properties
	 *
	 * @param {jQuery} select
	 *            a jQuery object that represents one or more select DOM elements
	 * @param {Object} objects
	 *            an array of objects with id and label properties.  Note the
	 *            labels may be resource bundle keys, and if they are they will
	 *            be resolved automatically
	 * @param {String|Number} [value]
	 *            the default value to select in the dropdown (null for an
	 *            option without a value, or undefined if it's not desirable to
	 *            specify a value)
	 * @param [options]
	 * 	          optional options to customize how the dropdown is rendered
	 * @param [options.sort=false]
	 *            indicates the options should be sorted alphabetically
	 * @param [options.selectedLabel]
	 * 			  If the selection must be made based on `option.label` instead
	 * 			  of the value this option can be provided.
	 * @param [options.localizeLabels=true]
	 * 			  If true, uses i18n on the labels. If false, renders them as
	 * 			  provided.
	 */
	idLabels: function(select, objects, value, options = {}) {
		let localizeLabels = options?.localizeLabels ?? true
		select = $(select)
		let optionEls = _.map(objects, function(d) {
			let label = localizeLabels ? i18n.get(d.label) : d.label
			var option = new Option(label, d.id)
			if (d.icon) {
				option.setAttribute('data-icon', d.icon)
			}
			if (d.preLabel) {
				option.setAttribute('data-pre-label', d.preLabel)
			}
			if (d.postLabel) {
				option.setAttribute('data-post-label', d.postLabel)
			}
			if (d.selected) {
				option.setAttribute('selected', d.selected)
			}
			if (d.data) {
				_.each(d.data, (val, key) => {
					option.setAttribute(`data-${key}`, val)
				})
			}
			if (d.id == value) {
				option.selected = true
			}
			// if we passed in a selectedLabel, then make it the selected option
			if (options.selectedLabel && d.label == options.selectedLabel) {
				value = undefined // must bypass value being cleared in commonBehavior()
				option.selected = true
			}
			return option
		})
		append(select, optionEls, options)
		this._commonBehavior(select, value)
	},


	/**
	 * Renders a dropdown using an object, so that the option values are the
	 * keys in the object and the option labels are the corresponding values
	 * for each key
	 *
	 * @param {jQuery} select
	 *            a jQuery object that represents one or more select DOM
	 *            elements
	 * @param {Object} hash
	 *            a plain JavaScript object. Note the values may be resource
	 *            bundle keys, and if they are they will be resolved
	 *            automatically
	 * @param {String|Number} [value]
	 *            the default value to select in the dropdown (null for an
	 *            option without a value, or undefined if it's not desirable to
	 *            specify a value)
	 * @param [options]
	 * 	          optional options to customize how the dropdown is rendered
	 * @param [options.sort=false]
	 *            indicates the options should be sorted alphabetically
	 */
	object: function(select, hash, value, options) {
		select = $(select)
		let optionEls = _.map(hash, (value, key) =>
			new Option(i18n.get(value), key))
		append(select, optionEls, options)
		this._commonBehavior(select, value)
	},

	/**
	 * Renders a dropdown of resource bundle keys, where the values for the
	 * options are the keys and the resolved message is the displayed text
	 *
	 * @param {jQuery} select
	 *            a jQuery object that represents one or more select DOM elements
	 * @param {String[]} keys
	 *            an array of resource bundle keys
	 * @param value
	 *            the default value to select in the dropdown (null for an
	 *            option without a value, or undefined if it's not desirable to
	 *            specify a value)
	 * @param [options]
	 * 	          optional options to customize how the dropdown is rendered
	 * @param [options.sort=false]
	 *            indicates the options should be sorted alphabetically
	 */
	keys: function(select, keys, value, options) {
		select = $(select)
		let optionEls = _.map(keys, key => {
			return new Option(i18n.get(key), key)
		})
		append(select, optionEls, options)
		this._commonBehavior(select, value)
	},

	/**
	 * Renders a dropdown of values, where the values are used for both the
	 * value and label for each Option.
	 *
	 * @param {jQuery} select
	 *            a jQuery object that represents one or more select DOM elements
	 * @param {String[]} values
	 *            an array of values to be used as both the label and value for
	 *            each HTML Option
	 * @param {String} value
	 *            the default value to select in the dropdown (null for an
	 *            option without a value, or undefined if it's not desirable to
	 *            specify a value)
	 * @param [options]
	 * 	          optional options to customize how the dropdown is rendered
	 * @param [options.sort=false]
	 *            indicates the options should be sorted alphabetically
	 */
	values: function(select, values, value, options) {
		select = $(select)
		let optionEls = _.map(values, value => {
			select.append(new Option(value, value))
		})
		append(select, optionEls, options)
		this._commonBehavior(select, value)
	},

	/**
	 * Renders a dropdown of currencies
	 */
	currency: function(select, value, options) {
		select = $(select)
		if (!_.get(options, 'omitDefault')) {
			select.append(new Option(i18n.get('common.default'), ''))
		}
		_.forEach(currency.all(), function(c) {
			var label = currency.label(c)
			select.append(new Option(label, c.code))
		})
		this._commonBehavior(select, value)
	},

	/**
	 * Renders a dropdown of missing value treatment options
	 *
	 * @param {jQuery} select - The select element to populate
	 * @param {string} [mvt] - The default MVT value to select
	 * @param {Object} [options] - Optional configuration
	 * @param {boolean} [options.includeNa=true] - Whether to include the 'not-applicable' option
	 */
	mvt: function(select, mvt, options = {}) {
		select = $(select)
		const includeNa = options.includeNa ?? true

		// Start with the first two options that are always included
		const mvtOptions = [
			{ id: 'entire-equation-blank', label: enums.missingValueTreatment['entire-equation-blank'] },
			{ id: 'zero', label: enums.missingValueTreatment.zero }
		]

		// Add the not-applicable option if includeNa is true
		if (includeNa) {
			mvtOptions.push({
				id: 'not-applicable',
				label: enums.missingValueTreatment['not-applicable']
			})
		}

		// Always add null as the last option
		mvtOptions.push({
			id: 'null',
			label: enums.missingValueTreatment.null
		})

		// #62291 Matt and Scott agreed over the phone on  1/28/25 that 'null'
		// is the correct default because that's what most
		// people would intuitively expect. There's a "Default MVT" setting in
		// app admin, but that is only for scorecards
		this.idLabels(select, mvtOptions, mvt ?? 'null')
	},

	/**
	 * Renders a dropdown of units
	 */
	unit: function(select, value) {
		select = $(select)
		select.append(new Option())
		this.values(select, config.units, value)
	},

	/**
	 * Renders a dropdown of scoring types with icons
	 * @param {Object} options
	 * @param {List<ScoringType>} [options.scoringTypes] list of scoring types to
	 * put in the dropdown; defaults to all possible scoring types
	 */
	scoringType: function(select, value, options) {
		let values = this.scoringTypeOptions(options?.scoringTypes)
		this.idLabels(select, values, value)
	},

	/**
	 * Returns the options to build a scoring types dropdown
	 *
	 * @param {List<ScoringType>} [scoringTypes] list of scoring types to build
	 * options for; defaults to all possible scoring types
	 */
	scoringTypeOptions: function(scoringTypes) {
		return _.map(scoringTypes ?? config.scoringTypes, scoringType  => {
			return {
				id: scoringType.id,
				label: i18n.get(scoringType.label),
				icon: scoringTypeIcon(scoringType)
			}
		})
	},

	/**
	 * Creates a dropdown to render the decimal precision
	 *
	 * @param {jQuery} select
	 *            a jQuery object that represents one or more select DOM elements
	 * @param value
	 *            the default value to select in the dropdown (null for an
	 *            option without a value, or undefined if it's not desirable to
	 *            specify a value)
	 */
	precision(select, value) {
		this.idLabels(select, precisionOptions(), value)
	}

}

/**
 * The value used for the "Default Decimal Precision" option
 */
const DEFAULT_PRECISION_VALUE = ''

/**
 * Returns the options for the decimal precision dropdown
 * @return {Array<{id: number|String, postLabel: String, label: String}>}
 */
function precisionOptions () {
	let options = _.map([0, 1, 2, 3, 4], i => {
		return {
			id: i,
			label: i18n.get(`scorecard.builder.decimalPrecision${i}`),
			postLabel: i18n.get(`scorecard.builder.decimalPrecision${i}.example`)
		}
	})
	options.unshift({
		id: DEFAULT_PRECISION_VALUE,
		label: i18n.get('scorecard.builder.decimalPrecision.default')
	})
	return options
}

export default dropdown
export { precisionOptions, DEFAULT_PRECISION_VALUE }

dropdown.__filename__ = 'components/dropdown.js'
